/**
 * Neon Auth React Provider
 * Provides authentication context using Neon Auth (powered by Better Auth)
 */

import React, {
  createContext,
  useContext,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import {
  neonClient,
  useSession,
  signIn,
  signUp,
  signOut,
  getSession,
  type NeonAuthSession,
  type NeonAuthUser,
} from './client';

/**
 * Auth context value type
 */
interface NeonAuthContextValue {
  // Session state
  user: NeonAuthUser | null;
  session: NeonAuthSession | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: Error | null;

  // Auth methods
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signInWithGoogle: (callbackURL?: string) => Promise<void>;
  signInWithGitHub: (callbackURL?: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string, name?: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const NeonAuthContext = createContext<NeonAuthContextValue | null>(null);

/**
 * Provider props
 */
interface NeonAuthProviderProps {
  children: ReactNode;
  onAuthStateChange?: (user: NeonAuthUser | null) => void;
}

/**
 * Neon Auth Provider Component
 * Wraps the application to provide authentication context
 */
export function NeonAuthProvider({
  children,
  onAuthStateChange,
}: NeonAuthProviderProps) {
  // Use the React adapter's useSession hook
  const sessionState = useSession();

  // Extract session data
  const session = (sessionState.data ?? null) as unknown as NeonAuthSession | null;
  const user = (session as any)?.user ?? null;
  const isLoading = sessionState.isPending;
  const error = sessionState.error ?? null;
  const isAuthenticated = !!user;

  // Notify on auth state changes
  React.useEffect(() => {
    if (onAuthStateChange && !isLoading) {
      onAuthStateChange(user);
    }
  }, [user, isLoading, onAuthStateChange]);

  // Sign in with email
  const signInWithEmail = useCallback(async (email: string, password: string) => {
    await signIn.email({ email, password });
  }, []);

  // Sign in with Google OAuth
  const signInWithGoogle = useCallback(async (callbackURL?: string) => {
    await signIn.social({
      provider: 'google',
      callbackURL: callbackURL ?? window.location.origin + '/dashboard',
    });
  }, []);

  // Sign in with GitHub OAuth
  const signInWithGitHub = useCallback(async (callbackURL?: string) => {
    await signIn.social({
      provider: 'github',
      callbackURL: callbackURL ?? window.location.origin + '/dashboard',
    });
  }, []);

  // Sign up with email
  const signUpWithEmail = useCallback(
    async (email: string, password: string, name?: string) => {
      await signUp.email({ email, password, name });
    },
    []
  );

  // Sign out
  const handleSignOut = useCallback(async () => {
    await signOut();
  }, []);

  // Refresh session
  const refreshSession = useCallback(async () => {
    await getSession();
  }, []);

  // Memoize context value
  const contextValue = useMemo<NeonAuthContextValue>(
    () => ({
      user,
      session,
      isLoading,
      isAuthenticated,
      error,
      signInWithEmail,
      signInWithGoogle,
      signInWithGitHub,
      signUpWithEmail,
      signOut: handleSignOut,
      refreshSession,
    }),
    [
      user,
      session,
      isLoading,
      isAuthenticated,
      error,
      signInWithEmail,
      signInWithGoogle,
      signInWithGitHub,
      signUpWithEmail,
      handleSignOut,
      refreshSession,
    ]
  );

  return (
    <NeonAuthContext.Provider value={contextValue}>
      {children}
    </NeonAuthContext.Provider>
  );
}

/**
 * Hook to access Neon Auth context
 * @throws Error if used outside of NeonAuthProvider
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useNeonAuth(): NeonAuthContextValue {
  const context = useContext(NeonAuthContext);
  if (!context) {
    throw new Error('useNeonAuth must be used within a NeonAuthProvider');
  }
  return context;
}

/**
 * Hook to get current user (shorthand)
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useNeonUser(): NeonAuthUser | null {
  const { user } = useNeonAuth();
  return user;
}

/**
 * Hook to check authentication status
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useNeonIsAuthenticated(): boolean {
  const { isAuthenticated } = useNeonAuth();
  return isAuthenticated;
}

/**
 * HOC to require authentication
 * Redirects to login if not authenticated
 */
// eslint-disable-next-line react-refresh/only-export-components
export function withNeonAuth<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  redirectTo = '/login'
): React.FC<P> {
  return function AuthenticatedComponent(props: P) {
    const { isAuthenticated, isLoading } = useNeonAuth();

    React.useEffect(() => {
      if (!isLoading && !isAuthenticated) {
        window.location.href = redirectTo;
      }
    }, [isAuthenticated, isLoading]);

    if (isLoading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      );
    }

    if (!isAuthenticated) {
      return null;
    }

    return <WrappedComponent {...props} />;
  };
}

/**
 * Component that only renders children if authenticated
 */
export function NeonAuthGuard({
  children,
  fallback,
}: {
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const { isAuthenticated, isLoading } = useNeonAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return fallback ?? null;
  }

  return <>{children}</>;
}

/**
 * Export the Neon client for direct access
 */
// eslint-disable-next-line react-refresh/only-export-components
export { neonClient };
